import {
  AutocompleteResult,
  Contact,
  CreateTaskInput,
  CreateTaskStatus,
  Entity,
  FromToType,
  PaymentFrequency,
  PaymentType,
  Task,
  TaskDirection,
  TaskType,
  UpdateContactInput,
  UpdateTaskInput,
  UpdateTaskStatus,
} from '@admiin-com/ds-graphql';
import { useSnackbar } from '@admiin-com/ds-web';
import { useApolloClient } from '@apollo/client';
import { FieldErrors, FieldValues, useFormContext } from 'react-hook-form';
import { useCreateContact } from '../../../hooks/useCreateContact/useCreateContact';
import { useCreateTask } from '../../../hooks/useCreateTask';
import { useUpdateContact } from '../../../hooks/useUpdateContact/useUpdateContact';
import { Bill } from '../PayBillCreateForm';
import { BillDraftSchema, BillSchema } from './schema';
import {
  transformDocumentAnalysisLineItemInput,
  transformS3UploadInput,
} from './transformer';
import { useEntitySelect } from '../../../features/hooks/useEntitySelect/useEntitySelect';
import { useUpdateTask } from '../../../hooks/useUpdateTask/useUpdateTask';
import { useEntityId } from '../../../features/hooks/useEntityContext/useEntityContext';
import useTasksFromUrl from '../../../features/hooks/useTasksFromUrl/useTasksFromUrlByFirmId';
import { getContactCreateInputFromBill } from './helpers';

export const useBillCreate = ({
  onErrorRow,
  onClose,
  draftTaskId,
}: {
  onErrorRow: ({
    index,
    errors,
  }: {
    index: number;
    errors: { path: string; message: string }[];
    fieldErrors?: FieldErrors<FieldValues>;
  }) => void;
  onClose: () => void;
  draftTaskId?: string;
}) => {
  const [createContact] = useCreateContact();
  const {
    trigger,
    formState: { errors },
    setError,
  } = useFormContext();

  const [updateContact] = useUpdateContact();
  const [createTask] = useCreateTask();
  const [updateTask] = useUpdateTask();
  const billAdapter = async (
    bill: Bill
  ): Promise<CreateTaskInput | UpdateTaskInput> => {
    const {
      from,
      to,
      amount,
      dueAt,
      paymentFrequency,
      issuedAt,
      lineItems,
      reference,
      document,
      documentAnalysis,
      saveBpayRefForFuture,
      bpay,
      bank,
      year,
    } = bill;
    let fromType: FromToType = FromToType.ENTITY;

    let newFrom: Contact | Entity | null | AutocompleteResult | undefined =
      null;
    if (from && to)
      if (!('id' in from)) {
        const bankName =
          from.name ??
          (from.firstName && from.lastName
            ? `${from.firstName} ${from.lastName}`
            : null);
        if (!bankName) {
          throw new Error('INVALID_BANK_NAME');
        }
        const createContactInput = getContactCreateInputFromBill(bill);
        const createdContactData = await createContact({
          variables: {
            input: createContactInput,
          },
        });
        newFrom = createdContactData.data.createContact;
        fromType = FromToType.CONTACT;
      } else {
        if (
          'type' in from &&
          (from.type === ('ENTITY' as any) || from.type === ('BPAY' as any))
        ) {
          newFrom = from;
          fromType = FromToType.ENTITY;
        } else {
          fromType = FromToType.CONTACT;
          if (bpay && saveBpayRefForFuture) {
            const isModified =
              bpay.billerCode !==
                documentAnalysis?.matchedContact?.bpay?.billerCode ||
              bpay.referenceNumber !==
                documentAnalysis?.matchedContact?.bpay?.referenceNumber;
            if (isModified) {
              const updateContactInput: UpdateContactInput = {
                id: from.id,
                bpay: {
                  billerCode: bpay.billerCode ?? null,
                  referenceNumber: bpay.referenceNumber ?? null,
                },
                entityId: (from as Contact).entityId,
              };
              const updatedContact = await updateContact({
                variables: {
                  input: updateContactInput,
                },
              });
              newFrom = updatedContact.data?.updateContact;
            } else {
              newFrom = from;
            }
          } else if (bank) {
            const isModified =
              bank.accountNumber !==
                documentAnalysis?.matchedContact?.bank?.accountNumber ||
              bank.routingNumber !==
                documentAnalysis?.matchedContact?.bank?.routingNumber;

            const newBank = {
              bankName: (from as Contact).bank?.bankName ?? null,
              holderType: (from as Contact).bank?.holderType ?? null,
              accountType: (from as Contact).bank?.accountType ?? null,
              country: (from as Contact).bank?.country ?? null,
              accountName:
                (from as Contact).bank?.accountName || from.name || '',
              accountNumber:
                bank.accountNumber ??
                (from as Contact).bank?.accountNumber ??
                '',
              routingNumber:
                bank.routingNumber ??
                (from as Contact).bank?.routingNumber ??
                '',
            };

            const fullDetailProvided =
              newBank.accountNumber && newBank.routingNumber;
            if (isModified && fullDetailProvided) {
              const updateContactInput: UpdateContactInput = {
                id: from.id,
                entityId: (from as Contact).entityId,
                bank: newBank,
              };
              const updatedContact = await updateContact({
                variables: {
                  input: updateContactInput,
                },
              });
              newFrom = updatedContact.data?.updateContact;
            } else {
              newFrom = from;
            }
          } else {
            newFrom = from;
          }
        }
      }
    else {
      newFrom = from as any;
    }

    if (!newFrom || !('id' in newFrom)) {
      throw new Error('From is required');
    }
    const isTax =
      (newFrom as AutocompleteResult)?.metadata?.subCategory === 'TAX';

    const task: CreateTaskInput = {
      fromId: newFrom?.id,
      fromType: fromType,
      toType: FromToType.ENTITY,
      toId: to?.id,
      amount,
      dueAt: dueAt,
      paymentTypes: [
        PaymentType.PAY_NOW,
        PaymentType.SCHEDULED,
        ...(isTax &&
        (paymentFrequency ?? PaymentFrequency.ONCE) === PaymentFrequency.ONCE
          ? [PaymentType.INSTALLMENTS]
          : []),
      ],
      issuedAt: issuedAt,
      documentAnalysisId: documentAnalysis?.id,
      lineItems: lineItems.map(transformDocumentAnalysisLineItemInput),
      reference: reference ?? '',
      documents: [transformS3UploadInput(document)],
      type: TaskType.PAY_ONLY,
      paymentFrequency: paymentFrequency ?? PaymentFrequency.ONCE,
      isUnlimitedRecurring: paymentFrequency !== PaymentFrequency.ONCE, //TODO: support number of payments - _old task logic
      direction: TaskDirection.RECEIVING,
      bpayReferenceNumber:
        (bpay?.billerCode ? bpay?.referenceNumber : null) ?? null,
      period: documentAnalysis?.task?.period ?? undefined,
      year: year ?? documentAnalysis?.task?.year ?? undefined,
    };

    return task;
  };

  const showSnackbar = useSnackbar();

  const createBill = async (bill: Bill, isDraft = false): Promise<Task> => {
    const task = await billAdapter(bill);
    if (isDraft) {
      task.status = CreateTaskStatus.DRAFT;
    }
    const createdTask = await createTask({ variables: { input: task } });
    return createdTask.data?.createTask;
  };

  const entityId = useEntityId();

  const updateBill = async (bill: Bill, isDraft = false): Promise<Task> => {
    const task = await billAdapter(bill);
    if (isDraft) {
      task.status = CreateTaskStatus.DRAFT;
    } else {
      task.status = UpdateTaskStatus.INCOMPLETE;
    }
    const updatedTask = await updateTask({
      variables: {
        input: {
          ...task,
          id: draftTaskId,
          entityId: entityId,
        },
      },
    });
    return updatedTask.data?.updateTask;
  };
  const { refetch } = useTasksFromUrl({ disableAutoFetch: true });

  const processBills = async (
    bills: Bill[],
    validationConfig: {
      schema: typeof BillSchema | typeof BillDraftSchema;
      validateFields: (index: number) => Promise<boolean>;
    },
    isDraft: boolean
  ) => {
    let noValidationErrors = false;
    try {
      // Validate all bills first
      let index = 0;
      for (const bill of bills) {
        if (!(await validationConfig.validateFields(index))) {
          onErrorRow({
            index,
            errors: [],
            fieldErrors: errors,
          });
          throw new Error(`Invalid bill ${index}`);
        }

        const result = validationConfig.schema.safeParse(bill);
        if (!result.success) {
          onErrorRow({
            index,
            errors: result.error.errors.map((err) => ({
              path: err.path.join('.'),
              message: err.message,
            })),
          });
          throw new Error(
            `Invalid bill ${index}: ${result.error.errors
              .map((err: any) => `${err.path.join('.')} - ${err.message}`)
              .join('; ')}`
          );
        }
        index++;
      }

      noValidationErrors = true;

      // Process the bills
      const tasks: Task[] = [];
      for (const bill of bills) {
        if (bill.checked) {
          try {
            if (draftTaskId) {
              const task = await updateBill(bill, isDraft);
              tasks.push(task);
            } else {
              const task = await createBill(bill, isDraft);
              tasks.push(task);
            }
          } catch (error: any) {
            if (error.message === 'INVALID_BANK_NAME') {
              onErrorRow({
                index: bills.indexOf(bill),
                errors: [
                  {
                    path: 'from',
                    message: 'Please create a valid contact',
                  },
                ],
                fieldErrors: {
                  bills: {
                    '0': {
                      from: {
                        type: 'custom',
                        message: 'Please create a valid contact',
                        ref: {
                          name: `bills.${bills.indexOf(bill)}.from`,
                          value: '',
                        },
                      },
                    },
                  },
                },
              });
              setError(`bills.${bills.indexOf(bill)}.from`, {
                type: 'custom',
                message: 'Please create a valid contact',
              });
              throw error;
            }
            throw error;
          }
        }
      }

      showSnackbar({
        message: draftTaskId
          ? 'Bills updated successfully'
          : 'Bills created successfully',
        severity: 'success',
        horizontal: 'right',
        vertical: 'bottom',
      });

      setTimeout(async () => {
        await postCreateBills(tasks);
      }, 1000);
      await refetch();
    } catch (error: any) {
      if (noValidationErrors) {
        showSnackbar({
          message:
            error?.message === 'INVALID_BANK_NAME'
              ? 'No ABN was detected, please create a new contact or set to an existing contact'
              : error?.message,
          severity: 'error',
          horizontal: 'right',
          vertical: 'bottom',
        });
      }
      throw error;
    }
  };

  const createBills = async (bills: Bill[]) => {
    await processBills(
      bills,
      {
        schema: BillSchema,
        validateFields: async (index) => await trigger(`bills.${index}`),
      },
      false
    );
  };

  const createBillsDraft = async (bills: Bill[]) => {
    await processBills(
      bills,
      {
        schema: BillDraftSchema,
        validateFields: async (index) =>
          (await trigger(`bills.${index}.from`)) &&
          (await trigger(`bills.${index}.to`)) &&
          (await trigger(`bills.${index}.dueAt`)),
      },
      true
    );
  };

  const postCreateBills = async (bills: Task[]) => {
    if (bills.length === 1) {
      await postCreateBill(bills[0]);
    } else {
      onClose();
    }
  };
  const apolloClient = useApolloClient();

  const selectEntity = useEntitySelect();
  const postCreateBill = async (task: Task) => {
    apolloClient.cache.evict({ fieldName: 'selectedEntityId' });
    apolloClient.cache.gc();
    selectEntity(task.entityId);
    onClose();
  };

  return {
    createBillsDraft,
    createBills,
  };
};
